import React, {useEffect, useState, Fragment} from "react";
import {Controller, useForm} from "react-hook-form";
import {AddressResponseData, CountriesFactory, ICountriesLookup, IStateLookup} from "@connox/typescript-library";
import {useTranslation} from "react-i18next";
import {useCheckoutContext} from "../../CheckoutContextProvider/CheckoutContextProvider";
import {FcInfo as InfoIcon} from "react-icons/fc";
import {getShopId} from "../../../lib/getShopId";
import Modal from "../../../lib/ModalWindow/Modal";
import {useCustomerContext} from "../../../customer/CustomerContextProvider/CustomerContextProvider";
import {isoLocales} from "../../../lib/payments/PaymentController";
import {isSameAddress} from "./AddressStep";
import {emailRegExp} from "../../../lib/ModalWindow/ForgotPasswordModal";
import styles from "./AddressForm.module.scss";
import selectStyles from "../../../newsletter/NewsLetterOptionsForm.module.scss";
import {useCartContext} from "../../../cart/CartContextProvider/CartContextProvider";
import Select from "../../../lib/form/Select/Select";

export declare type IAddressFormData = AddressResponseData & {
  vatNumber: string | null;
};

const AddressForm: React.FC<{
  onChange: (data: IAddressFormData) => void;
  onValid: (isValid: boolean) => void;
  triggerErrors?: boolean;
  countries: ICountriesLookup[];
  isDelivery?: boolean;
  phoneError: string; // unexpected phone error comes from server with 400 responce
  lastAddress: IAddressFormData;
  // eslint-disable-next-line complexity
}> = ({onChange, onValid, triggerErrors = false, isDelivery = false, phoneError, countries, lastAddress}) => {
  const {t} = useTranslation("checkout");
  const {customer} = useCustomerContext();
  const {deliveryAddress, invoiceAddress} = useCheckoutContext().checkout;
  const {isCorporateCustomer, vatNumber, countryCode} = useCheckoutContext().checkout.cartData;
  const {viewState} = useCartContext();
  const [states, setStates] = useState<IStateLookup[]>([]);
  const [isZipValid, setIsZipValid] = useState(true);
  const [isEmailValid, setIsEmailValid] = useState(true);
  // phone error comes with address data
  const [addressPhoneError, setAddressPhoneError] = useState(phoneError);
  const defaultInvoiceCountryCode = isSameAddress(deliveryAddress, invoiceAddress)
    ? countryCode
    : invoiceAddress?.countryCode ?? countryCode;

  const gender = (isDelivery ? deliveryAddress?.gender : invoiceAddress?.gender) || lastAddress.gender;
  const email =
    invoiceAddress?.email || lastAddress.email || customer.email || viewState.partials?.partialAddress.email || "";

  const defaultAddress: IAddressFormData = {
    ...((isDelivery ? deliveryAddress : undefined) || invoiceAddress || lastAddress),
    vatNumber,
    countryCode: isDelivery ? countryCode : defaultInvoiceCountryCode,
    gender,
    email,
  };

  const MAX_LENGTH = 50;

  const {
    register,
    watch,
    formState: {errors, isValid},
    trigger,
    setValue,
    reset,
    control,
  } = useForm<IAddressFormData>({
    mode: "onChange",
    defaultValues: defaultAddress,
  });

  useEffect(() => {
    if (!invoiceAddress) {
      reset(defaultAddress);
    }
  }, [lastAddress]);

  useEffect(() => {
    onValid(isValid);
  }, [isValid]);

  useEffect(() => {
    if (triggerErrors) {
      void trigger();
    }
  }, [triggerErrors]);

  // this effect will update the error state for zip and email after every call
  useEffect(() => {
    setIsZipValid(!defaultAddress.messages?.some((msg) => msg.field === "zip") ?? true);
    setIsEmailValid(!defaultAddress.messages?.some((msg) => msg.field === "email") ?? true);
  }, [defaultAddress.messages]);

  useEffect(() => {
    setIsZipValid(true);
  }, [countryCode]);

  // this effect will keep tracking the phone validation.
  useEffect(() => {
    if (watch().phone) {
      if (!phoneError) {
        setAddressPhoneError(
          defaultAddress.messages?.find((msg) => msg.type === "error" && msg.code.includes("PHONE"))?.code || "",
        );
      } else setAddressPhoneError(phoneError);
    } else {
      setAddressPhoneError("");
    }
  }, [defaultAddress.messages, phoneError]);

  // this effect will initialize country value and states in case the counrty in the default address in not valid for shipping
  useEffect(() => {
    if (!countries.some((country) => country.countryCode === defaultAddress.countryCode)) {
      setValue("countryCode", DefaultCountryCode[getShopId()]);
      setStates([]);
    }
  }, [countries]);

  const updateStates = async () => {
    const selectedCountry = countries.find((country) => country.countryCode === watch().countryCode);
    if (selectedCountry && selectedCountry.isStateRequired) {
      const statesData = await CountriesFactory.getStates(getShopId(), watch().countryCode);
      setStates(statesData);
      setValue("state", defaultAddress.state);
      void (await trigger("state"));
    } else {
      setValue("state", "");
      setStates([]);
    }
    onChange(watch());
  };

  // this effect will get states after every change to the country
  useEffect(() => {
    void updateStates();
  }, [watch().countryCode]);

  const MobileIcon: React.FC = () => {
    const [open, setOpen] = useState(false);

    return (
      <Fragment>
        <InfoIcon
          className={styles.infoIcon}
          onClick={() => {
            setOpen(!open);
          }}
        />
        <Modal isVisible={open} closeFunction={() => setOpen(false)}>
          <p>{t("Ihre Telefonnummer benötigen wir für eventuelle Rückfragen ...")}</p>
        </Modal>
      </Fragment>
    );
  };

  const zipMaxLength = (): number => {
    switch (watch().countryCode) {
      case "AT":
      case "CH":
        return 4;
      case "DE":
        return 5;
      default:
        return 20;
    }
  };

  const updateForm = () => {
    onChange(watch());
  };

  return (
    <div>
      <form className={styles.addressForm} onChange={updateForm}>
        {isCorporateCustomer && (
          <div className={styles.inputWrapper}>
            <input
              placeholder={t("Firma")}
              {...register("company", {
                maxLength: {value: MAX_LENGTH, message: t("Bitte maximal {{count}} Zeichen", {count: MAX_LENGTH})},
              })}
            />
            {errors.company && <span>{errors.company.message}</span>}
          </div>
        )}

        {!gender && (
          <div className={styles.formRow}>
            <div className={`${styles.inputWrapper} ${styles.small}`}>
              <Controller
                control={control}
                render={({field}) => (
                  <Select
                    styles={{wrapper: selectStyles.select}}
                    onChange={(value) => {
                      field.onChange(value);
                      updateForm();
                    }}
                    placeHolder={t("Anrede", {ns: "customerAccount"})}
                    options={[
                      {value: "F", text: t("Frau")},
                      {value: "M", text: t("Herr")},
                    ]}
                  />
                )}
                name={"gender"}
                rules={{required: t<string>("Bitte geben Sie Ihr Geschlecht an")}}
              />
              {errors.gender && (
                <span className={styles.error} style={{whiteSpace: "nowrap"}}>
                  {errors.gender.message}
                </span>
              )}
            </div>
          </div>
        )}

        <div className={styles.formRow}>
          <div className={`${styles.inputWrapper} ${styles.medium}`}>
            <input
              placeholder={t(isCorporateCustomer ? "z. Hd. Vorname *" : "Vorname *")}
              {...register("firstName", {
                required: t<string>("Bitte ausfüllen"),
                maxLength: {value: MAX_LENGTH, message: t("Bitte maximal {{count}} Zeichen", {count: MAX_LENGTH})},
              })}
            />
            {errors.firstName && <span className={styles.error}>{errors.firstName.message}</span>}
          </div>

          <div className={`${styles.inputWrapper} ${styles.medium}`}>
            <input
              placeholder={t(isCorporateCustomer ? "z. Hd. Nachname *" : "Nachname *")}
              {...register("lastName", {
                required: t<string>("Bitte ausfüllen"),
                maxLength: {value: MAX_LENGTH, message: t("Bitte maximal {{count}} Zeichen", {count: MAX_LENGTH})},
              })}
            />
            {errors.lastName && <span className={styles.error}>{errors.lastName.message}</span>}
          </div>
        </div>

        {isCorporateCustomer && (
          <div className={styles.inputWrapper}>
            <input
              placeholder={t("Abteilung")}
              {...register("department", {
                maxLength: {value: MAX_LENGTH, message: t("Bitte maximal {{count}} Zeichen", {count: MAX_LENGTH})},
              })}
            />
            {errors.department && <span className={styles.error}>{errors.department.message}</span>}
          </div>
        )}

        <div
          className={`${styles.formRow} ${
            ["GB", "FR", "US"].includes(DefaultCountryCode[getShopId()]) ? styles.reverse : ""
          }`}
        >
          <div className={`${styles.inputWrapper} ${styles.big}`}>
            <input placeholder={t("Straße *")} {...register("street", {required: t<string>("Bitte ausfüllen")})} />
            {errors.street && <span className={styles.error}>{errors.street.message}</span>}
          </div>

          <div className={`${styles.inputWrapper} ${styles.small}`}>
            <input placeholder={t("Nr. *")} {...register("number", {required: t<string>("Bitte ausfüllen")})} />
            {errors.number && <span className={styles.error}>{errors.number.message}</span>}
          </div>
        </div>

        <div className={styles.formRow}>
          <div
            className={`${styles.inputWrapper} ${styles.small}`}
            onChange={() => {
              setIsZipValid(true);
            }}
          >
            <input
              placeholder={t("PLZ *")}
              {...register("zip", {
                required: true,
                maxLength: zipMaxLength(),
              })}
            />

            {(!isZipValid || errors.zip?.type === "maxLength") && watch().zip && (
              <span className={styles.error}>
                {t("Bitte gültige PLZ angeben").replace("zip", isoLocales[getShopId()] === "en_GB" ? "post" : "zip")}
              </span>
            )}

            {errors.zip?.type === "required" && <span className={styles.error}>{t("Bitte ausfüllen")}</span>}
          </div>

          <div className={`${styles.inputWrapper} ${styles.big}`}>
            <input
              placeholder={t("Stadt *")}
              {...register("city", {
                required: t<string>("Bitte ausfüllen"),
                maxLength: {value: MAX_LENGTH, message: t("Bitte maximal {{count}} Zeichen", {count: MAX_LENGTH})},
              })}
            />
            {errors.city && <span className={styles.error}>{errors.city.message}</span>}
          </div>
        </div>

        <div className={styles.formRow}>
          <div
            className={styles.inputWrapper}
            onChange={() => {
              setAddressPhoneError("");
            }}
          >
            <input type={"tel"} placeholder={t("Telefonnummer")} {...register("phone", {})} />
            {!!addressPhoneError && <span className={styles.error}>{t(addressPhoneError)}</span>}
          </div>
          {isCorporateCustomer && <MobileIcon />}
        </div>

        {!customer.isLoggedIn && !isDelivery && (
          <div
            className={styles.inputWrapper}
            onChange={() => {
              setIsEmailValid(true);
            }}
          >
            <input
              type={"email"}
              placeholder={t("Ihre E-Mail-Adresse *")}
              {...register("email", {
                required: true,
                pattern: emailRegExp,
              })}
            />
            {errors.email?.type === "required" && <span className={styles.error}>{t("Bitte ausfüllen")}</span>}
            {(errors.email?.type === "pattern" || !isEmailValid) && watch().email && (
              <span className={styles.error}>{t("Bitte geben Sie eine gültige E-Mail-Adresse an")}</span>
            )}
          </div>
        )}
        {countries.length > 0 && (
          <div
            className={styles.inputWrapper}
            onChange={() => {
              setIsZipValid(true);
            }}
          >
            <select
              value={watch().countryCode}
              {...register("countryCode", {
                required: true,
              })}
              style={{color: !watch().countryCode ? "#a1a1a1" : "unset"}}
            >
              <option key="headline" value="" disabled={true}>
                {t(`Bitte wählen Sie ein ${isDelivery ? "Lieferland" : "Land"} *`)}
              </option>
              {countries.map((country) => {
                return (
                  <option key={country.countryCode} value={country.countryCode}>
                    {country.name}
                  </option>
                );
              })}
            </select>
            {errors.countryCode && <span className={styles.error}>{t("Bitte wählen Sie ein Land *")}</span>}
          </div>
        )}

        {isCorporateCustomer && !isDelivery && (
          <div className={styles.inputWrapper}>
            <input placeholder={t("USt.-ID")} {...register("vatNumber")} />
          </div>
        )}
        {states.length > 0 && (
          <div className={styles.inputWrapper}>
            <select
              value={watch().state || ""}
              {...register("state", {required: true})}
              style={{color: !watch().state ? "#a1a1a1" : "unset"}}
            >
              <option key="headline" value="" disabled={true}>
                {t("Bitte wählen Sie ein Staat aus")}
              </option>
              {states.map((state, index) => {
                return (
                  <option key={index} value={state.stateCode}>
                    {state.name}
                  </option>
                );
              })}
            </select>
            {errors.state && <span className={styles.error}>{t("Bitte wählen Sie einen Staat *")}</span>}
          </div>
        )}
      </form>
    </div>
  );
};

export const DefaultCountryCode: {[shopID: number]: string} = {
  100001: "DE",
  100009: "AT",
  100017: "CH",
  100027: "CH",
  100005: "US",
  100020: "GB",
  100008: "FR",
  100022: "DK",
  100029: "NL",
};

export default AddressForm;
